/* Cloud Endpoints dashboard widget (CMP-4067 phase 2).
 *
 * Drives the server-rendered Cloud Endpoints datatable (templates/dashboard/
 * cloud_endpoints_widget.html), which is hosted inside the Vue VueDjangoTemplate
 * widget. Responsibilities beyond the plain c2 datatable:
 *   - resolve each row's connection status via a bounded async "verify" fan-out
 *     (the read endpoint returns no status by design), degrading to "unknown"
 *     on timeout / error, and caching results across redraws (sort/search).
 *   - wire the per-row Verify / Sync actions to the v3 domain endpoints.
 *
 * jQuery POSTs pick up the CSRF header from c2/init.js's global $.ajaxSetup; we
 * also pass the freshly-rendered token from the fragment as a belt-and-suspenders.
 */
(function (c2) {
  "use strict";

  var VERIFY_CONCURRENCY = 4;
  var VERIFY_TIMEOUT_MS = 8000;

  // Connection status keyed by globalId, shared across redraws so a sort/search
  // (which re-renders the cells) doesn't re-verify already-resolved handlers.
  var statusCache = {};

  var PILL = {
    checking: { cls: "ce-conn--checking", label: "Checking…" },
    connected: {
      cls: "ce-conn--connected",
      label: "Connected",
      icon: "bi bi-cloud-check text-success",
    },
    disconnected: {
      cls: "ce-conn--disconnected",
      label: "Disconnected",
      icon: "bi bi-cloud-fill text-danger",
    },
    unknown: {
      cls: "ce-conn--unknown",
      label: "Unknown",
      icon: "bi bi-cloud text-secondary portal-secondary",
    },
  };

  function renderPill($span, status) {
    var p = PILL[status] || PILL.unknown;
    // Show only the icon (or spinner while checking); the status text lives in
    // the tooltip rather than inline.
    var marker =
      status === "checking"
        ? '<span class="ce-conn__spinner" aria-hidden="true"></span>'
        : '<i class="ce-conn__icon ' + p.icon + '" aria-hidden="true"></i>';
    Object.keys(PILL).forEach(function (k) {
      $span.removeClass(PILL[k].cls);
    });
    $span
      .addClass("ce-conn " + p.cls)
      .attr("data-status", status)
      .attr("title", p.label)
      .html(marker);
  }

  function ajaxOpts(opts, url) {
    var o = { url: url, type: "POST", dataType: "json", timeout: VERIFY_TIMEOUT_MS };
    if (opts.csrfToken) o.headers = { "X-CSRFToken": opts.csrfToken };
    return o;
  }

  // Resolve one handler's status. 2xx with a boolean `connected` maps directly;
  // 2xx without one is treated as connected; timeout/503/error -> "unknown".
  function verifyOne(opts, globalId) {
    var url = opts.verifyUrlTemplate.replace("{globalId}", globalId);
    return $.ajax(ajaxOpts(opts, url)).then(
      function (body) {
        if (body && typeof body.connected === "boolean") {
          return body.connected ? "connected" : "disconnected";
        }
        return "connected";
      },
      function () {
        return "unknown";
      },
    );
  }

  // Bounded-concurrency worker pool over a list (mirrors resourceHandlerVerify.js).
  function runPool(items, concurrency, worker) {
    var i = 0;
    function next() {
      if (i >= items.length) return $.Deferred().resolve().promise();
      return worker(items[i++]).then(next);
    }
    var runners = [];
    var n = Math.min(concurrency, items.length);
    for (var c = 0; c < n; c++) runners.push(next());
    return $.when.apply($, runners);
  }

  function applyStatus($table, gid, status) {
    statusCache[gid] = status;
    $table.find('.ce-conn[data-global-id="' + gid + '"]').each(function () {
      renderPill($(this), status);
    });
  }

  // Resolve every connection cell currently in the table. Cached statuses are
  // re-applied instantly; only unresolved, verifiable handlers are fetched.
  function verifyVisible($table, opts) {
    var toVerify = [];
    $table.find(".ce-conn").each(function () {
      var $span = $(this);
      var gid = $span.attr("data-global-id");
      if (statusCache[gid]) {
        renderPill($span, statusCache[gid]);
        return;
      }
      if ($span.attr("data-can-verify") !== "true") {
        applyStatus($table, gid, "unknown");
        return;
      }
      renderPill($span, "checking");
      toVerify.push(gid);
    });
    runPool(toVerify, VERIFY_CONCURRENCY, function (gid) {
      return verifyOne(opts, gid).then(function (status) {
        applyStatus($table, gid, status);
      });
    });
  }

  function reVerifyOne($table, opts, gid) {
    delete statusCache[gid];
    applyStatus($table, gid, "checking");
    // applyStatus cached "checking"; clear it so verifyVisible re-fetches later.
    delete statusCache[gid];
    verifyOne(opts, gid).then(function (status) {
      applyStatus($table, gid, status);
    });
  }

  function syncOne($table, opts, gid, $btn) {
    var url = opts.syncUrlTemplate.replace("{globalId}", gid);
    $btn.prop("disabled", true);
    $.ajax(ajaxOpts(opts, url)).always(function () {
      $btn.prop("disabled", false);
      var dt = $table.DataTable();
      if (dt && dt.ajax) dt.ajax.reload(null, false); // keep paging; refresh Last sync
    });
  }

  function init(selector, opts) {
    opts = opts || {};
    var $table = $(selector);
    if (!$table.length || !c2.dataTables) return;

    // Show 5 rows per page. 5 isn't in the c2 default lengthMenu, so add it (a
    // pageLength absent from lengthMenu makes DataTables emit a console warning).
    // Server-side (via data-table-source); the JSON view applies search/sort/paging.
    c2.dataTables.init(selector, {
      pageLength: 5,
      lengthMenu: [5, 10, 25, 50],
    });

    // Verify on every draw (initial load, sort, search, paginate). Bound after
    // init so the first (async) server-side draw is caught.
    $table.on("draw.dt", function () {
      verifyVisible($table, opts);
    });

    // Delegated row actions (rows are re-rendered each draw).
    $table.on("click", ".ce-verify", function () {
      var gid = $(this).closest(".ce-actions").attr("data-global-id");
      if (gid) reVerifyOne($table, opts, gid);
    });
    $table.on("click", ".ce-sync", function () {
      var gid = $(this).closest(".ce-actions").attr("data-global-id");
      if (gid) syncOne($table, opts, gid, $(this));
    });
  }

  c2.dashboardCloudEndpoints = { init: init };
})(window.c2);
